import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '@/components/Portal';
import { useDismissable } from '@/lib/useDismissable';
import {
  ChevronDownIcon,
  HeadphonesOffIcon,
  HeadphonesIcon,
  LogOutIcon,
  MicOffIcon,
  MicIcon,
  VolumeIcon,
} from '@/components/icons';

/**
 * Cursor-anchored right-click menu for a voice participant — the moderation
 * actions that used to live as hover chips on the tile. Same Portal +
 * useLayoutEffect clamp pattern as the server/message context menus.
 *
 * Each action is only rendered when the caller has the matching permission
 * (computed in VoiceChannelStub via canModerateParticipant). "Переместить"
 * expands an inline submenu of the guild's other voice channels.
 */
export function VoiceModerationMenu(props: {
  x: number;
  y: number;
  onClose: () => void;
  canMute: boolean;
  canDeaf: boolean;
  canKick: boolean;
  serverMuted: boolean;
  serverDeaf: boolean;
  moveTargets: { id: string; name: string | null }[];
  onServerMute: () => void;
  onServerDeaf: () => void;
  onKick: () => void;
  onMove: (channelId: string) => void;
}) {
  const { t: tr } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [moveExpanded, setMoveExpanded] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const m = 8;
    let nx = props.x;
    let ny = props.y;
    if (nx + r.width + m > window.innerWidth) nx = props.x - r.width;
    if (ny + r.height + m > window.innerHeight) ny = window.innerHeight - r.height - m;
    nx = Math.min(Math.max(m, nx), window.innerWidth - r.width - m);
    ny = Math.max(m, ny);
    setPos({ x: nx, y: ny });
  }, [props.x, props.y, moveExpanded]);

  useDismissable(true, ref, props.onClose);

  const canMove = props.moveTargets.length > 0;

  return (
    <Portal>
      <div
        ref={ref}
        style={
          pos ? { left: pos.x, top: pos.y } : { left: -9999, top: -9999, visibility: 'hidden' }
        }
        className="bg-surface fixed z-[100] max-h-[calc(100dvh-16px)] min-w-[220px] max-w-[calc(100vw-16px)] overflow-y-auto rounded-xl border border-separator py-1.5 shadow-card"
        role="menu"
      >
        {props.canMute && (
          <MenuItem
            icon={props.serverMuted ? <MicIcon className="h-4 w-4" /> : <MicOffIcon className="h-4 w-4" />}
            onClick={() => {
              props.onServerMute();
              props.onClose();
            }}
          >
            {props.serverMuted ? tr('voice.serverMuteOff') : tr('voice.serverMuteOn')}
          </MenuItem>
        )}

        {props.canDeaf && (
          <MenuItem
            icon={
              props.serverDeaf ? (
                <HeadphonesIcon className="h-4 w-4" />
              ) : (
                <HeadphonesOffIcon className="h-4 w-4" />
              )
            }
            onClick={() => {
              props.onServerDeaf();
              props.onClose();
            }}
          >
            {props.serverDeaf ? tr('voice.serverDeafOff') : tr('voice.serverDeafOn')}
          </MenuItem>
        )}

        {canMove && (
          <>
            <button
              type="button"
              role="menuitem"
              onClick={() => setMoveExpanded((v) => !v)}
              className="group/ctx mx-1 flex min-h-10 w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm text-ink transition-colors hover:bg-default/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [@media(pointer:coarse)]:min-h-11"
            >
              <VolumeIcon className="h-4 w-4 shrink-0 text-ink-muted transition-colors group-hover/ctx:text-accent" />
              <span className="flex-1 truncate">{tr('voice.moveTo')}</span>
              <ChevronDownIcon
                className={
                  'h-3.5 w-3.5 shrink-0 text-ink-muted transition-transform ' +
                  (moveExpanded ? 'rotate-0' : '-rotate-90')
                }
              />
            </button>
            {moveExpanded && (
              <div className="my-0.5 ml-5 border-l border-separator pl-1">
                {props.moveTargets.map((t) => (
                  <MenuItem
                    key={t.id}
                    onClick={() => {
                      props.onMove(t.id);
                      props.onClose();
                    }}
                  >
                    {t.name ?? tr('voiceBar.channelFallback')}
                  </MenuItem>
                ))}
              </div>
            )}
          </>
        )}

        {props.canKick && (
          <>
            <div className="h-2" role="separator" />
            <MenuItem
              icon={<LogOutIcon className="h-4 w-4" />}
              danger
              onClick={() => {
                props.onKick();
                props.onClose();
              }}
            >
              {tr('voice.disconnectMember')}
            </MenuItem>
          </>
        )}
      </div>
    </Portal>
  );
}

function MenuItem(props: {
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={props.onClick}
      className={
        'group/ctx mx-1 flex min-h-10 w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm transition-colors hover:bg-default/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [@media(pointer:coarse)]:min-h-11 ' +
        (props.danger ? 'text-danger' : 'text-ink')
      }
    >
      {props.icon && (
        <span
          className={
            'shrink-0 transition-colors ' +
            (props.danger ? 'text-danger' : 'text-ink-muted group-hover/ctx:text-accent')
          }
        >
          {props.icon}
        </span>
      )}
      <span className="flex-1 truncate">{props.children}</span>
    </button>
  );
}
